import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import axios from '../../../../config/axios'
import InfiniteSelect from '../../../../components/InfiniteSelect'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import plus from '../../../../assets/certificates/AddParameter/blue-plus.svg'
import save from '../../../../assets/certificates/AddParameter/save.svg'
import trash from '../../../../assets/equipment/Tables/trash.svg'
import searchIcon from '../../../../assets/router/search.svg'
import crosslight from '../../../../assets/stock/FiltersHeader/cross.svg'
import {
  MainContainer,
  StyledCard,
  FormGrid,
  StyledFormGroup,
  ToggleSwitchContainer,
  SwitchLabel,
  Slider,
  SwitchInput,
  AddButton,
  DeleteButton,
  ActionButtons,
  CancelButton,
  SaveButton,
  SubHeaderContainer,
  SectionName,
  LeftSide,
  BreadcrumbsContainer,
  CardTitle,
  GradeRow,
  GradeHeader,
  GradeLabel,
  GradeOptions,
  SizeButton,
  AddButtonWrapper,
  FamilyTagsContainer,
  FamilyTag,
  RemoveTagButton,
} from './style'

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const FASHION_API_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

interface GradeType { id: number; name: string }
interface Grade { id: number; name: string; typeId: number; type?: GradeType; GraduationType?: GradeType }
interface MaterialType { id: number; name: string }
interface ResourceCategory { id: number; name: string }
interface Resource { id: number; name: string; description?: string; categoryId: number; materialId?: number | null }
interface Group { id: number; name: string }
interface GroupItem { id: number; groupId: number; name: string }
interface MaterialItem { id: number; materialId: number | null; materialName: string; categoryMaterialIds: Record<number, number | null> }

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({ items }) => (
  <BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </BreadcrumbsContainer>
)

const RegisterCollection: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEditMode = Boolean(id)
  const { setFeedback } = useContext(FeedbackContext)

  const [collectionName, setCollectionName] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingData, setLoadingData] = useState<boolean>(true)

  const [gradesByType, setGradesByType] = useState<Record<string, Grade[]>>({})
  const [enabledGradeTypes, setEnabledGradeTypes] = useState<Record<string, boolean>>({})
  const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([])

  const [allGroups, setAllGroups] = useState<Group[]>([])
  const [groups, setGroups] = useState<GroupItem[]>([])
  const [groupSearch, setGroupSearch] = useState<string>('')
  const [newGroupName, setNewGroupName] = useState<string>('')

  const [allResourceCategories, setAllResourceCategories] = useState<ResourceCategory[]>([])
  const [resourcesByCategory, setResourcesByCategory] = useState<Record<number, Resource[]>>({})
  const [materialNameById, setMaterialNameById] = useState<Record<number, string>>({})

  const [materials, setMaterials] = useState<MaterialItem[]>([])
  const [collectionColors, setCollectionColors] = useState<{ id: number | string; name: string; hex?: string }[]>([])
  const [allColorOptions, setAllColorOptions] = useState<{ id: number | string; name: string; hex?: string }[]>([])
  const [colorUsageByFamily, setColorUsageByFamily] = useState<Record<string, string[]>>({})
  const [colorSearch, setColorSearch] = useState<string>('')
  

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingData(true)
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }
      let loadedResourcesByCategory: Record<number, Resource[]> = {}

      let byType: Record<string, Grade[]> = {}
      let enabledTypes: Record<string, boolean> = {}
      try {
        const gradesRes = await axios.get(`${FASHION_API_URL}/grades`, { headers })
        const grades: Grade[] = Array.isArray(gradesRes.data) ? gradesRes.data : []
        byType = {}
        enabledTypes = {}
        grades.forEach((grade) => {
          const typeName = (grade.GraduationType || grade.type)?.name
          if (!typeName) return
          if (!byType[typeName]) {
            byType[typeName] = []
            enabledTypes[typeName] = false
          }
          byType[typeName].push(grade)
        })
        Object.keys(byType).forEach((typeName) => {
          byType[typeName].sort((a, b) => {
            const aNum = parseInt(a.name)
            const bNum = parseInt(b.name)
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
            return a.name.localeCompare(b.name)
          })
        })
        setGradesByType(byType)
        setEnabledGradeTypes(enabledTypes)
      } catch (err) {
        console.error('Erro ao carregar grades:', err)
      }

      try {
        const groupsRes = await axios.get(`${FASHION_API_URL}/groups`, { headers })
        const groupsData: Group[] = Array.isArray(groupsRes.data) ? groupsRes.data : []
        setAllGroups(groupsData)
      } catch (err) {
        console.error('Erro ao carregar grupos:', err)
      }

      try {
        const categoriesRes = await axios.get(`${FASHION_API_URL}/resource-categories`, { headers })
        const categories: ResourceCategory[] = Array.isArray(categoriesRes.data) ? categoriesRes.data : []
        setAllResourceCategories(categories)
      } catch (err) {
        console.error('Erro ao carregar resource categories:', err)
      }

      try {
        const resourcesRes = await axios.get(`${FASHION_API_URL}/resources`, { headers })
        const resources: Resource[] = Array.isArray(resourcesRes.data) ? resourcesRes.data : []
        const byCategory: Record<number, Resource[]> = {}
        resources.forEach((resource) => {
          if (resource.categoryId) {
            if (!byCategory[resource.categoryId]) byCategory[resource.categoryId] = []
            byCategory[resource.categoryId].push(resource)
          }
        })
        loadedResourcesByCategory = byCategory
        setResourcesByCategory(byCategory)
      } catch (err) {
        console.error('Erro ao carregar resources:', err)
      }

      try {
        const materialsRes = await axios.get(`${GATEWAY_URL}/backend/material/list`, { headers })
        const allMaterials = Array.isArray(materialsRes.data) ? materialsRes.data : []
        const mapById: Record<number, string> = {}
        allMaterials.forEach((m: any) => {
          if (typeof m?.id === 'number') mapById[m.id] = String(m?.name || '')
        })
        setMaterialNameById(mapById)
      } catch (err) {
        console.error('Erro ao carregar materiais (mapa):', err)
      }

      if (isEditMode && id) {
        try {
          const collectionRes = await axios.get(`${FASHION_API_URL}/collections/${id}`, { headers })
          const collection = collectionRes.data
          setCollectionName(collection.name || '')

          if (collection.CollectionColor && Array.isArray(collection.CollectionColor)) {
            const cols = collection.CollectionColor.map((c: any) => ({ id: c.id || `${c.name}-${Math.random()}`, name: c.name || '', hex: c.hex || '#ffffff' }))
            setCollectionColors(cols)
            setAllColorOptions(cols)
          }

          try {
            const familiesRes = await axios.get(`${FASHION_API_URL}/collections/${id}/families`, { headers })
            const families = Array.isArray(familiesRes.data) ? familiesRes.data : []
            const usage: Record<string, string[]> = {}

            families.forEach((family: any) => {
              const familyName = String(family?.name || '').trim()
              const familyColors = Array.isArray(family?.FamilyColor) ? family.FamilyColor : []
              familyColors.forEach((fc: any) => {
                const colorId = fc?.CollectionColor?.id
                if (typeof colorId !== 'number') return
                const key = String(colorId)
                if (!usage[key]) usage[key] = []
                if (familyName && !usage[key].includes(familyName)) usage[key].push(familyName)
              })
            })

            setColorUsageByFamily(usage)
          } catch (err) {
            console.error('Erro ao carregar uso de cores por família:', err)
            setColorUsageByFamily({})
          }

          const collectionGrades = collection.CollectionGrade || collection.CollectionGrades || collection.GradeInCollection || collection.GradeInProduct || collection.gradeInProduct || collection.gradeInCollection || []
          if (Array.isArray(collectionGrades) && collectionGrades.length > 0) {
            const gradeIdsFromCollection: number[] = collectionGrades
              .map((g: any) => g.gradeId || g.Grade?.id || g.id)
              .map((v: any) => Number(v))
              .filter((n: number) => !Number.isNaN(n) && n > 0)

            if (gradeIdsFromCollection.length > 0) {
              setSelectedGradeIds(gradeIdsFromCollection)

              const typesToEnable: Record<string, boolean> = {}
              const sourceByType = Object.keys(byType).length ? byType : gradesByType
              Object.keys(sourceByType).forEach((typeName) => {
                const gradesForType: Grade[] = sourceByType[typeName] || []
                const hasAny = gradesForType.some((gr: Grade) => gradeIdsFromCollection.includes(Number(gr.id)))
                if (hasAny) typesToEnable[typeName] = true
              })

              setEnabledGradeTypes((prev) => ({ ...prev, ...typesToEnable }))
            }
          }

          const collectionGroups = collection.CollectionGroup || collection.GroupInCollection || collection.GroupInCollections || collection.GroupInCollections || []
          if (Array.isArray(collectionGroups) && collectionGroups.length > 0) {
            const groupItems: GroupItem[] = collectionGroups.map((g: any, idx: number) => ({
              id: idx + 1,
              groupId: Number(g.groupId || g.Group?.id || g.GroupId || g.id) || 0,
              name: (g.Group && (g.Group.name || g.GroupName)) || g.name || ''
            }))
            setGroups(groupItems)
          }

          if (collection.CollectionMaterial && Array.isArray(collection.CollectionMaterial)) {
            const materialItems: MaterialItem[] = collection.CollectionMaterial.map((mat: any, index: number) => {
              const resources = mat.CollectionMaterialResources || []
              const categoryMaterialIds: Record<number, number | null> = {}
              resources.forEach((r: any) => {
                if (typeof r?.categoryResourceId === 'number' && typeof r?.materialId === 'number') {
                  categoryMaterialIds[r.categoryResourceId] = r.materialId
                }
              })

              return {
                id: index + 1,
                materialId: mat.materialId || mat.Material?.id || null,
                materialName: mat.Material?.name || '',
                categoryMaterialIds,
              }
            })
            setMaterials(materialItems)
          }
        } catch (error: any) {
          console.error('Erro ao carregar coleção:', error)
          alert('Erro ao carregar dados da coleção')
        }
      }

      setLoadingData(false)
    }

    fetchInitialData()
  }, [id, isEditMode])

  const fetchMaterials = useCallback(async (limit: number, offset: number, search?: string) => {
    const token = localStorage.getItem('token')
    const res = await axios.get(`${GATEWAY_URL}/backend/material/list/stock`, { headers: { Authorization: `Bearer ${token}` } })
    const allMaterials = Array.isArray(res.data) ? res.data : []
    const normalizedSearch = (search || '').trim().toLowerCase()
    const filtered = normalizedSearch
      ? allMaterials.filter((m: any) => String(m?.name || '').toLowerCase().includes(normalizedSearch))
      : allMaterials
    const start = Math.max(0, Number(offset) || 0)
    const end = start + Math.max(0, Number(limit) || 0)
    return filtered.slice(start, end)
  }, [])

  const toggleGradeType = (typeName: string, enabled: boolean) => {
    setEnabledGradeTypes((prev) => ({ ...prev, [typeName]: enabled }))
    if (!enabled) {
      const gradeIdsOfType = gradesByType[typeName]?.map((g) => g.id) || []
      setSelectedGradeIds((prev) => prev.filter((id) => !gradeIdsOfType.includes(id)))
    }
  }

  const toggleGrade = (gradeId: number, typeName: string) => {
    if (!enabledGradeTypes[typeName]) return
    setSelectedGradeIds((prev) => (prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]))
  }

  const removeGroup = (id: number) => setGroups((prev) => prev.filter((g) => g.id !== id))
  const removeCollectionColor = (id: number | string) => setCollectionColors((prev) => prev.filter((c) => c.id !== id))

  const availableGroupOptions = useMemo(() => {
    const selectedIds = new Set(groups.map((g) => g.groupId))
    const normalizedSearch = groupSearch.trim().toLowerCase()

    return allGroups
      .filter((g) => !selectedIds.has(g.id))
      .filter((g) => normalizedSearch ? g.name.toLowerCase().includes(normalizedSearch) : true)
      .map((g) => ({ id: g.id, name: g.name }))
  }, [allGroups, groups, groupSearch])

  const availableColorOptions = useMemo(() => {
    const selectedIds = new Set(collectionColors.map((c) => String(c.id)))
    const normalizedSearch = colorSearch.trim().toLowerCase()

    return allColorOptions
      .filter((c) => !selectedIds.has(String(c.id)))
      .filter((c) => normalizedSearch ? c.name.toLowerCase().includes(normalizedSearch) : true)
      .map((c) => ({ id: c.id, name: c.name, hex: c.hex }))
  }, [allColorOptions, collectionColors, colorSearch])

  const addMaterial = () => {
    const newId = materials.length > 0 ? Math.max(...materials.map((m) => m.id)) + 1 : 1
    setMaterials((prev) => [...prev, { id: newId, materialId: null, materialName: '', categoryMaterialIds: {} }])
  }

  const removeMaterial = (id: number) => setMaterials((prev) => prev.filter((m) => m.id !== id))

  const selectMaterial = (materialItemId: number, mat: MaterialType | null) => {
    setMaterials((prev) => prev.map((m) => (m.id === materialItemId ? { ...m, materialId: mat?.id || null, materialName: mat?.name || '' } : m)))
  }

  const addGroup = async () => {
    const nameToCreate = newGroupName?.trim()
    if (!nameToCreate) return
    try {
      const token = localStorage.getItem('token')
      const res = await axios.post(`${FASHION_API_URL}/groups`, { name: nameToCreate }, { headers: { Authorization: `Bearer ${token}` } })
      const created = res.data
      if (created && created.id) {
        const newId = groups.length > 0 ? Math.max(...groups.map((g) => g.id)) + 1 : 1
        setGroups((prev) => [...prev, { id: newId, groupId: created.id, name: created.name }])
        setNewGroupName('')
      }
    } catch (err) {
      console.error('Erro ao criar grupo:', err)
      alert('Falha ao criar grupo')
    }
  }

  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collectionName.trim()) {
      alert('Por favor, preencha o nome da coleção.')
      return
    }
    if (materials.length === 0) {
      alert('É obrigatório cadastrar pelo menos um conjunto de Tecidos e Aviamentos.')
      return
    }
    const invalidMaterial = materials.find((m) => !m.materialId)
    if (invalidMaterial) {
      alert('Todos os conjuntos devem ter um Tecido (Material) selecionado.')
      return
    }
    setLoading(true)

    const materialsPayload = materials.filter((m) => m.materialId).map((m) => {
      const categoryMaterials = Object.entries(m.categoryMaterialIds || {})
        .map(([categoryIdRaw, selectedMaterialIdRaw]) => {
          const categoryId = Number(categoryIdRaw)
          const selectedMaterialId = Number(selectedMaterialIdRaw)
          if (!categoryId || !selectedMaterialId) return null
          return { categoryId, materialId: selectedMaterialId }
        })
        .filter((v): v is { categoryId: number; materialId: number } => v !== null)

      return {
        materialId: m.materialId,
        categoryMaterials,
      }
    })
    const selectedGroupIds = groups.filter((g) => g.groupId > 0).map((g) => g.groupId)

    const payload = {
      name: collectionName,
      gradeIds: selectedGradeIds,
      groupIds: selectedGroupIds,
      materials: materialsPayload,
      collectionColors: collectionColors.map((c) => ({
        id: typeof c.id === 'number' ? c.id : undefined,
        name: c.name,
      })),
    }

    try {
      const token = localStorage.getItem('token')
      if (isEditMode && id) {
        await axios.put(`${FASHION_API_URL}/collections/${id}`, payload, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } })
        setFeedback({ isOpen: true, statusOk: true, message: 'Coleção atualizada com sucesso!' })
      } else {
        await axios.post(`${FASHION_API_URL}/collections`, payload, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } })
        setFeedback({ isOpen: true, statusOk: true, message: 'Coleção criada com sucesso!' })
      }
      navigate('/panel/collections')
    } catch (err: any) {
      console.error('Erro ao salvar coleção:', err)
      alert(err.response?.data?.message || 'Erro ao salvar coleção. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Produtos manufaturados', path: '/panel/collections' },
    { label: isEditMode ? 'Editar coleção' : 'Cadastrar coleção' },
  ]

  if (loadingData) return (
    <MainContainer>
      <p>Carregando dados...</p>
    </MainContainer>
  )

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName>{isEditMode ? 'Editar coleção' : 'Nova coleção'}</SectionName>
        </LeftSide>
        <ActionButtons>
          <CancelButton type='button' onClick={() => navigate('/panel/collections')}>Cancelar</CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={loading}><img src={save} alt='Salvar' />{loading ? 'Salvando...' : 'Salvar coleção'}</SaveButton>
        </ActionButtons>
      </SubHeaderContainer>

      <Breadcrumbs items={breadcrumbItems} />

      <MainContainer>
        <form onSubmit={handleSubmit}>
          <StyledCard>
            <CardTitle>Geral</CardTitle>
            <FormGrid cols={3}>
              <StyledFormGroup>
                <label htmlFor='collectionName'>Nome da coleção</label>
                <input type='text' id='collectionName' value={collectionName} onChange={(e) => setCollectionName(e.target.value)} placeholder='Digite aqui' autoComplete='off' />
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Selecionar grupos</label>
                <InfiniteSelect
                  placeholder='Procure grupos'
                  mode='crud'
                  endpointConfig={{ baseUrl: FASHION_API_URL, listPath: '/groups', createPath: '/groups', deletePath: '/groups', searchParam: 'search' }}
                  allowCreate
                  allowDelete
                  showCreateOnlyIfNotExists
                  minCreateLength={2}
                  options={availableGroupOptions}
                  searchValue={groupSearch}
                  onSearchChange={(search) => setGroupSearch(search)}
                  onSelect={(option) => {
                    if (!option) return
                    const selectedId = Number(option.id)
                    if (selectedId && !groups.some((g) => g.groupId === selectedId)) {
                      setGroups((prev) => [...prev, { id: prev.length > 0 ? Math.max(...prev.map(p => p.id)) + 1 : 1, groupId: selectedId, name: option.name }])
                      setGroupSearch('')
                    }
                  }}
                  onItemCreate={async (name: string) => {
                    const token = localStorage.getItem('token')
                    const response = await axios.post(
                      `${FASHION_API_URL}/groups`,
                      { name },
                      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                    )

                    const created = response.data
                    if (created?.id) {
                      setAllGroups((prev) => prev.some((g) => g.id === created.id) ? prev : [...prev, { id: created.id, name: created.name }])
                    }

                    setGroupSearch('')

                    return { id: created.id, name: created.name }
                  }}
                  onItemDelete={async (id) => {
                    try {
                      const numericId = Number(id)
                      await axios.delete(`${FASHION_API_URL}/groups/${numericId}`)
                      setGroups((prev) => prev.filter((g) => g.groupId !== numericId))
                      setAllGroups((prev) => prev.filter((g) => g.id !== numericId))
                    } catch (err) {
                      console.error('Erro ao excluir grupo via InfiniteSelect:', err)
                      throw err
                    }
                  }}
                  getOptionLabel={(opt) => opt.name}
                />

                {groups.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {groups.map((group) => (
                      <FamilyTag key={group.id}>
                        {group.name}
                        <RemoveTagButton onClick={() => removeGroup(group.id)}><img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} /></RemoveTagButton>
                      </FamilyTag>
                    ))}
                  </FamilyTagsContainer>
                )}
              </StyledFormGroup>

              <StyledFormGroup>
                <label>Cores da coleção</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <InfiniteSelect
                      placeholder={'Cores da coleção'}
                      searchPlaceholder='Buscar cor...'
                      mode='crud'
                      options={availableColorOptions}
                      searchValue={colorSearch}
                      onSearchChange={(search) => setColorSearch(search)}
                      endpointConfig={isEditMode && id ? {
                        baseUrl: FASHION_API_URL,

                        listPath: `/collections/${id}`,
                        searchParam: 'search',
                        transformResponse: (data: any) => {
                          const cols = Array.isArray(data?.CollectionColor) ? data.CollectionColor : []
                          return cols.map((c: any) => ({ id: c.id, name: c.name, hex: c.hex }))
                        }
                      } : undefined}
                      allowCreate
                      allowDelete
                      showCreateOnlyIfNotExists
                      minCreateLength={2}

                      onSelect={(option) => {
                        if (!option) return
                        const exists = collectionColors.some((c) => String(c.id) === String(option.id))
                        if (!exists) {
                          setCollectionColors((prev) => [...prev, { id: option.id, name: option.name, hex: option.hex }])
                        }

                        const existsInOptions = allColorOptions.some((c) => String(c.id) === String(option.id))
                        if (!existsInOptions) {
                          setAllColorOptions((prev) => [...prev, { id: option.id, name: option.name, hex: option.hex }])
                        }
                        setColorSearch('')
                      }}

                      onItemCreate={async (name: string) => {
                        const trimmedName = String(name || '').trim()
                        if (!trimmedName) {
                          throw new Error('Nome da cor é obrigatório')
                        }

                        let newItem: { id: number | string; name: string }

                        if (isEditMode && id) {
                          const token = localStorage.getItem('token')
                          const res = await axios.post(
                            `${FASHION_API_URL}/collections/${id}/colors`,
                            { name: trimmedName },
                            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
                          )
                          const created = res.data
                          newItem = { id: created.id, name: created.name }
                        } else {
                          const token = localStorage.getItem('token')
                          const res = await axios.post(
                            `${FASHION_API_URL}/colors`,
                            { name: trimmedName },
                            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
                          )
                          const created = res.data
                          newItem = { id: created.id, name: created.name }
                        }

                        setAllColorOptions((prev) => [...prev, newItem])
                        setColorSearch('')

                        return newItem as any
                      }}

                      onItemDelete={async (itemId) => {
                        const familiesUsingColor = colorUsageByFamily[String(itemId)] || []
                        if (familiesUsingColor.length > 0) {
                          const colorName = collectionColors.find((c) => String(c.id) === String(itemId))?.name || 'esta cor'
                          throw new Error(`Não é possível remover ${colorName}, pois está vinculada à(s) família(s): ${familiesUsingColor.join(', ')}`)
                        }

                        const numericColorId = Number(itemId)
                        if (isEditMode && id && Number.isFinite(numericColorId)) {
                          const token = localStorage.getItem('token')
                          await axios.delete(`${FASHION_API_URL}/collections/${id}/colors/${numericColorId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                          })
                        }

                        setAllColorOptions((prev) => prev.filter((c) => String(c.id) !== String(itemId)))
                        setCollectionColors((prev) => prev.filter((c) => String(c.id) !== String(itemId)))
                        setColorUsageByFamily((prev) => {
                          const next = { ...prev }
                          delete next[String(itemId)]
                          return next
                        })
                      }}
                      getOptionLabel={(opt) => opt.name}
                    />
                  </div>
                </div>

                {collectionColors.length > 0 && (
                  <FamilyTagsContainer style={{ marginTop: '8px' }}>
                    {collectionColors.map((c) => (
                      <FamilyTag key={c.id}>
                        {c.name}
                        <RemoveTagButton onClick={() => removeCollectionColor(c.id)}><img src={crosslight} alt='Remover' style={{ width: 10, height: 10 }} /></RemoveTagButton>
                      </FamilyTag>
                    ))}
                  </FamilyTagsContainer>
                )}
              </StyledFormGroup>
            </FormGrid>
          </StyledCard>

          <StyledCard>
            <CardTitle>Grades</CardTitle>
            {Object.keys(gradesByType).length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: 14 }}>Nenhuma grade cadastrada no sistema.</p>
            ) : (
              Object.entries(gradesByType).map(([typeName, grades]) => (
                <GradeRow key={typeName}>
                  <GradeHeader>
                    <ToggleSwitchContainer>
                      <SwitchLabel htmlFor={`grade-${typeName}`}>
                        <SwitchInput type='checkbox' id={`grade-${typeName}`} checked={enabledGradeTypes[typeName] || false} onChange={(e) => toggleGradeType(typeName, e.target.checked)} />
                        <Slider className='slider' />
                      </SwitchLabel>
                    </ToggleSwitchContainer>
                    <GradeLabel>{typeName}</GradeLabel>
                  </GradeHeader>
                  <GradeOptions>
                    {grades.map((grade) => (
                      <SizeButton key={grade.id} type='button' disabled={!enabledGradeTypes[typeName]} className={selectedGradeIds.includes(grade.id) ? 'selected' : ''} onClick={() => toggleGrade(grade.id, typeName)}>{grade.name}</SizeButton>
                    ))}
                  </GradeOptions>
                </GradeRow>
              ))
            )}
          </StyledCard>

          <StyledCard>
            <CardTitle>Tecidos e Aviamentos (Conjuntos)</CardTitle>
            {materials.map((material) => (
              <div key={material.id} style={{ padding: 16, backgroundColor: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', overflowX: 'auto', paddingBottom: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                  <StyledFormGroup style={{ flex: '0 0 220px', marginBottom: 0 }}>
                    <label>Tecido (Material)</label>
                    <div style={{ position: 'relative' }}>
                      <InfiniteSelect placeholder='Procure aqui' mode='readonly' fetchFunction={fetchMaterials} value={material.materialId ? { id: material.materialId, name: material.materialName } : null} onSelect={(option) => selectMaterial(material.id, option)} getOptionLabel={(opt) => opt.name} />
                      {!material.materialId && (<span style={{ position: 'absolute', right: 30, top: 12, pointerEvents: 'none', zIndex: 1 }}><img src={searchIcon} alt='Buscar' style={{ width: 16, height: 16, opacity: 0.6 }} /></span>)}
                    </div>
                  </StyledFormGroup>

                  {allResourceCategories.map((category) => {
                    const resources = resourcesByCategory[category.id] || []
                    const uniqueMaterials = Array.from(
                      new Map(
                        resources
                          .filter((r) => typeof r.materialId === 'number' && r.materialId !== null)
                          .map((r) => [
                            Number(r.materialId),
                            {
                              id: Number(r.materialId),
                              name: materialNameById[Number(r.materialId)] || `Material ${Number(r.materialId)}`,
                            },
                          ])
                      ).values()
                    ).sort((a, b) => a.name.localeCompare(b.name))

                    const selectedCategoryMaterialId = material.categoryMaterialIds?.[category.id] || ''
                    return (
                      <StyledFormGroup key={category.id} style={{ flex: '0 0 180px', marginBottom: 0 }}>
                        <label>{category.name}</label>
                        <select value={selectedCategoryMaterialId} onChange={(e) => {
                          const selectedId = e.target.value ? Number(e.target.value) : null
                          setMaterials((prev) => prev.map((m) => {
                            if (m.id === material.id) {
                              return {
                                ...m,
                                categoryMaterialIds: {
                                  ...(m.categoryMaterialIds || {}),
                                  [category.id]: selectedId,
                                },
                              }
                            }
                            return m
                          }))
                        }}>
                          <option value=''>Selecione</option>
                          {uniqueMaterials.map((mat) => (<option key={mat.id} value={mat.id}>{mat.name}</option>))}
                        </select>
                      </StyledFormGroup>
                    )
                  })}

                  <DeleteButton type='button' onClick={() => removeMaterial(material.id)} style={{ marginTop: 24 }}><img src={trash} alt='Excluir' /></DeleteButton>
                </div>
              </div>
            ))}

            <AddButtonWrapper>
              <AddButton type='button' onClick={addMaterial}><img src={plus} alt='Adicionar' />Adicionar conjunto</AddButton>
            </AddButtonWrapper>
          </StyledCard>
        </form>

      </MainContainer>
    </>
  )
}

export default RegisterCollection